import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBaseUrl } from "../../../../../Redux/Config/AppConfig";
import { toast } from "react-toastify";

const FormRoom = ({ selectedRoom, setSelectedRoom, refreshData }) => {
  const BaseUrl = getBaseUrl();
  const dispatch = useDispatch();
  const closeBtnRef = useRef(null);
  const [formData, setFormData] = useState({
    room_number: "",
    room_type: "",
    room_status: "",
    floor_number: "",
    standard_checkout: "",
  });

  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypesLoaded, setRoomTypesLoaded] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchRoomTypes = async () => {
    let token = localStorage.getItem("token");
    let hotel_id = localStorage.getItem("hotel_id");

    fetch(`${BaseUrl}/room/all-room-types?hotel_id=${hotel_id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({hotel_id}),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setRoomTypes(data?.data || []);
      })
      .catch((error) => {
        console.error("Error fetching room types:", error);
      });
  };

  const handleRoomTypeClick = () => {
    if (!roomTypesLoaded) {
      fetchRoomTypes();
      setRoomTypesLoaded(true);
    }
  };

  useEffect(() => {
    if (!roomTypesLoaded) {
      fetchRoomTypes();
      setRoomTypesLoaded(true);
    }
  }, [selectedRoom]);

  useEffect(() => {
    if (
      selectedRoom &&
      selectedRoom.room_number !== undefined &&
      selectedRoom.room_type !== undefined &&
      selectedRoom.room_status !== undefined
    ) {
      setFormData({
        room_number: selectedRoom.room_number?.toString() || "",
        room_type: selectedRoom?.room_id || selectedRoom.room_type || "",
        room_status: selectedRoom.room_status || "",
        floor_number: selectedRoom.floor_number?.toString() || "",
        standard_checkout: selectedRoom.standard_checkout || "",
      });
    } else {
      handleClear();
    }
  }, [selectedRoom]);

  const validate = () => {
    const newErrors = {};
    if (!formData.room_number.trim())
      newErrors.room_number = "Room number is required.";
    if (!formData.room_type) newErrors.room_type = "Room type is required.";
    if (!formData.room_status)
      newErrors.room_status = "Room status is required.";
    if (!formData.floor_number || isNaN(formData.floor_number))
      newErrors.floor_number = "Valid floor number is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    if (validate()) {
      try {
        let token = localStorage.getItem("token");

        let hotel_id= localStorage.getItem('hotel_id');
        formData.hotel_id=hotel_id;
        const url = selectedRoom
          ? `${BaseUrl}/room/update-room-number/${selectedRoom.id}`
          : `${BaseUrl}/room/create-room-number`;
        const method = selectedRoom ? "PATCH" : "POST";
        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();
        
        if (!response.ok) {
          // Show the actual API error response
          const errorMessage = result?.message || result?.error || `HTTP ${response.status}: ${response.statusText}`;
          throw new Error(errorMessage);
        }

        document.querySelector('[data-bs-dismiss="offcanvas"]')?.click();
        closeBtnRef.current?.click();

        handleClear();

        if (refreshData) refreshData();
        if (setSelectedRoom) setSelectedRoom(null);
        toast.success(
          `Room ${selectedRoom ? "updated" : "created"} successfully!`
        );
      } catch (error) {
        console.error("Error submitting room:", error);
        // Show the actual error message from the API response
        const errorMessage = error.message || `Failed to ${selectedRoom ? "update" : "create"} room. Please try again.`;
        toast.error(errorMessage);
      }
    }
  };
  const handleClear = () => {
    setFormData({
      room_number: "",
      room_type: "",
      room_status: "",
      floor_number: "",
      standard_checkout: "",
    });
    setErrors({});
  };

  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="add-room"
      aria-labelledby="add-room"
    >
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">
          {selectedRoom ? "Edit Room" : "Add Room"}
        </h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          ref={closeBtnRef}
          onClick={() => {
            setSelectedRoom(null);
            handleClear();
          }}
        ></button>
      </div>
      <div className="offcanvas-body">
        <form>
          <div className="form-group mb-3">
            <label className="form-label text-muted">
              Room Number{" "}
              {!selectedRoom && <span className="text-danger">*</span>}
            </label>
            <input
              type="text"
              name="room_number"
              className={`form-control ${
                errors.room_number ? "is-invalid" : ""
              }`}
              placeholder="Enter Room Number"
              value={formData.room_number}
              onChange={handleChange}
              disabled={!!selectedRoom}
            />
            {errors.room_number && (
              <div className="invalid-feedback">{errors.room_number}</div>
            )}
          </div>

          <div className="form-group mb-3">
            <label className="form-label text-muted">
              Room Type{" "}
              {!selectedRoom && <span className="text-danger">*</span>}
            </label>
            <select
              name="room_type"
              className={`form-select ${errors.room_type ? "is-invalid" : ""}`}
              value={formData.room_type}
              onChange={handleChange}
              onClick={handleRoomTypeClick}
            >
              <option value="">Select Room Type</option>
              {roomTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.room_type}
                </option>
              ))}
            </select>
            {errors.room_type && (
              <div className="invalid-feedback">{errors.room_type}</div>
            )}
          </div>

          <div className="form-group mb-3">
            <label className="form-label text-muted">
              Room Status{" "}
              {!selectedRoom && <span className="text-danger">*</span>}
            </label>
            <select
              name="room_status"
              className={`form-select ${
                errors.room_status ? "is-invalid" : ""
              }`}
              value={formData.room_status}
              onChange={handleChange}
            >
              <option value="">Choose Room Status</option>
              <option value="available">Available</option>
              <option value="cleaning in progress">cleaning in progress</option>
              <option value="maintenance">Maintainance</option>
              <option value="occupied">Occupied</option>
              <option value="out of order">Out of order</option>
            </select>
            {errors.room_status && (
              <div className="invalid-feedback">{errors.room_status}</div>
            )}
          </div>

          <div className="form-group mb-3">
            <label className="form-label text-muted">
              Floor Number{" "}
              {!selectedRoom && <span className="text-danger">*</span>}
            </label>
            <input
              type="number"
              name="floor_number"
              className={`form-control ${
                errors.floor_number ? "is-invalid" : ""
              }`}
              placeholder="Enter Floor Number"
              value={formData.floor_number}
              onChange={handleChange}
            />
            {errors.floor_number && (
              <div className="invalid-feedback">{errors.floor_number}</div>
            )}
          </div>

          <div className="form-group mb-3">
            <label className="form-label text-muted">Standard Checkout</label>
            <input
              type="time"
              name="standard_checkout"
              className={`form-control ${
                errors.standard_checkout ? "is-invalid" : ""
              }`}
              value={formData.standard_checkout}
              onChange={handleChange}
            />
            {errors.standard_checkout && (
              <div className="invalid-feedback">{errors.standard_checkout}</div>
            )}
          </div>
        </form>

        <div className="d-flex">
          <button
            type="button"
            className="btn w-100 me-1 py-2 btn-primary"
            onClick={handleClear}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn w-100 ms-1 py-2 btn-dark"
            onClick={handleSubmit}
          >
            {selectedRoom ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormRoom;
